import type { ProviderData } from "@/classes/Self-Serve/SelfServeDataGenerator";
import type { AppContext, DayInRDE, DaySlot, HourGroup, RDEInDay, RDESlot, SlotExtended } from "@/types/scheduling";

import { format, parse } from "date-fns";
import { groupBy } from "ramda";

export const handleApplySlotsFilter = <T extends AppContext>(context: T) => {
  console.debug("provider_data", context.provider_data);

  if (!context.provider_data_raw) {
    console.error("provider_data_raw is undefined");
    return {
      ...context,
      applied_slot_filers: true,
    };
  }

  if (!context.slots || context.slots.length === 0) {
    console.error("no slots available when filtering");
    return { ...context, applied_slot_filters: true };
  }

  const timeStr = "HH:mm:ss";
  const tzStr = "XX";
  const dateStr = "yyyy-MM-dd";
  const fullDateStr = `${dateStr} ${timeStr} ${tzStr}`;
  const getDateObj = (date: string, short = false) =>
    short ? parse(date, dateStr, new Date()) : parse(date, fullDateStr, new Date());
  const getDayOfWeek = (date: string, short = false) => format(getDateObj(date, short), "E");
  const getDayOfMonth = (date: string, short = false) => format(getDateObj(date, short), "MMM dd");
  const getHour = (date: string) => format(getDateObj(date), "h:mm a");

  const byDay = groupBy((slot: SlotExtended) => slot.short_date);
  const byRDE = groupBy((slot: SlotExtended) => slot.user_id);
  const byHour = groupBy((slot: SlotExtended) => getHour(slot.date));

  // TODO find a way to remove dedupRDEData and keep the V2 version while avoiding the rdeSlots dependency
  const dedupRDEDataV2 = Array.from(
    context.provider_data_raw
      .reduce((map, item) => {
        const key = item.id || item.healthie_id;
        if (!map.has(key)) {
          map.set(key, item);
        }
        return map;
      }, new Map())
      .values(),
  ) as ProviderData[];

  // extract same_day for each RDE from dedupRDEDataV2
  const sameDayData = dedupRDEDataV2.reduce(
    (acc, item) => {
      if (item.same_day) {
        acc[item.healthie_id] = item.same_day;
      }
      return acc;
    },
    {} as Record<string, boolean>,
  );

  let holidaySlotsRemoved = 0;
  let d2cIntervalSlotsRemoved = 0;
  let d2cWeekendSlotsRemoved = 0;
  let weekendSlotsRemoved = 0;
  let sameDaySlotsRemoved = 0;
  let over65SlotsRemoved = 0;

  const filteredSlots = context.slots.filter((slot) => {
    const slotDate = parse(slot.date, "yyyy-MM-dd HH:mm:ss XX", new Date());
    const now = new Date();
    const hoursDiff = (slotDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    const pathname = window.location.pathname;

    const isD2CFlow = pathname.includes("/appointment/select");

    const slotMonth = slotDate.getMonth();
    const slotDay = slotDate.getDate();
    const slotMinutes = slotDate.getMinutes();
    const slotWeekDay = slotDate.getDay();

    // Filter out holiday dates: Dec 24-25, 28-29, 31 and Jan 1
    if (
      (slotMonth === 0 && slotDay === 1) ||
      (slotMonth === 11 && (slotDay === 24 || slotDay === 25 || slotDay === 28 || slotDay === 29 || slotDay === 31))
    ) {
      holidaySlotsRemoved++;
      return false;
    }

    // filter out 15/45 min intervals when in the D2C flow
    if (isD2CFlow && (slotMinutes === 15 || slotMinutes === 45)) {
      d2cIntervalSlotsRemoved++;
      return false;
    }

    // filter out weekends when in the D2C flow
    if (isD2CFlow && (slotWeekDay === 0 || slotWeekDay === 6)) {
      d2cWeekendSlotsRemoved++;
      return false;
    }

    // If weekend - Not on the same day and at the earliest on Monday.
    if (now.getDay() === 0 || now.getDay() === 6) {
      weekendSlotsRemoved++;
      const slotLocalStringEST = slotDate.toLocaleString("en-US", { timeZone: "America/New_York" });
      const slotTimeEST = new Date(slotLocalStringEST);
      const slotHoursEST = slotTimeEST.getHours();
      console.debug({
        slotDate,
        slotLocalStringEST,
        slotTimeEST,
        slotHoursEST,
      });
      return slotWeekDay > 1 || (slotWeekDay === 1 && slotHoursEST >= 12);
    }

    if (context.prior_data.quiz_instance?.answers?.age) {
      const age = context.prior_data.quiz_instance.answers.age;
      if (parseInt(age) > 65) {
        if (hoursDiff < 96) {
          over65SlotsRemoved++;
          return false;
        }
      }
    }

    if (sameDayData[slot.user_id]) {
      sameDaySlotsRemoved++;
      return hoursDiff >= 24; // We had it as 3 hours, but changing it here to >= 24 would eliminate same_day appointments altogether
    } else {
      return hoursDiff >= 24; // Keep if 24+ hours from now
    }
  });

  // TODO should this be sent to Posthog?
  console.debug("total slots:", context.slots.length);
  console.debug("holiday slots conditions applied:", holidaySlotsRemoved);
  console.debug("d2c interval slots conditions applied:", d2cIntervalSlotsRemoved);
  console.debug("d2c weekend slots conditions applied:", d2cWeekendSlotsRemoved);
  console.debug("general weekend slots conditions applied:", weekendSlotsRemoved);
  console.debug("over 65 slots removed:", over65SlotsRemoved);
  console.debug("same day slots conditions applied:", sameDaySlotsRemoved);
  console.debug("total slots excluded:", context.slots.length - filteredSlots.length);
  console.debug("slots result:", filteredSlots.length);

  const grouppedByDay = byDay(filteredSlots);
  const grouppedByRDE = byRDE(filteredSlots);

  const rdeSlots = Object.entries(grouppedByRDE)
    .map(([rde, rdeSlots]) => {
      if (rdeSlots && rdeSlots.length > 0) {
        const dayGroups = Object.entries(byDay(rdeSlots))
          .map(([day, daySlots]) => {
            if (daySlots && daySlots.length > 0) {
              const firstSlot = daySlots[0];
              const hourGroups = Object.entries(byHour(daySlots))
                .map(([hour, hourSlots]) => {
                  if (Array.isArray(hourSlots) && hourSlots.length > 0) {
                    const firstHourSlot = hourSlots[0];
                    return {
                      hour: getHour(firstHourSlot.date),
                      slot: firstHourSlot,
                    };
                  }
                  return null;
                })
                .filter((group): group is HourGroup => group !== null);

              return {
                day,
                dayOfWeek: getDayOfWeek(firstSlot.date),
                dayOfMonth: getDayOfMonth(firstSlot.date),
                hours: hourGroups,
                slotCount: hourGroups.length,
              };
            }
            return null;
          })
          .filter((group): group is DayInRDE => group !== null);

        return {
          rde,
          days: dayGroups,
          dayCount: dayGroups.length,
          slotCount: dayGroups.reduce((acc, group) => acc + group.slotCount, 0),
        };
      }
      return null;
    })
    .filter((group): group is RDESlot => group !== null);

  const daySlots = Object.entries(grouppedByDay)
    .map(([day, daySlots]) => {
      if (daySlots && daySlots.length > 0) {
        const firstSlot = daySlots[0];
        const rdeGroups = Object.entries(byRDE(daySlots))
          .map(([rde, rdeSlots]) => {
            if (rdeSlots && rdeSlots.length > 0) {
              const hourGroups = Object.entries(byHour(rdeSlots))
                .map(([hour, hourSlots]) => {
                  if (Array.isArray(hourSlots) && hourSlots.length > 0) {
                    const firstHourSlot = hourSlots[0];
                    return {
                      hour: getHour(firstHourSlot.date),
                      slot: firstHourSlot,
                    };
                  }
                  return null;
                })
                .filter((group): group is HourGroup => group !== null);

              return {
                rde,
                hours: hourGroups,
                slotCount: hourGroups.length,
              };
            }
            return null;
          })
          .filter((group): group is RDEInDay => group !== null);

        return {
          day,
          dayOfWeek: getDayOfWeek(firstSlot.date),
          dayOfMonth: getDayOfMonth(firstSlot.date),
          rdes: rdeGroups,
          rdeCount: rdeGroups.length,
          slotCount: rdeGroups.reduce((acc, group) => acc + group.slotCount, 0),
        };
      }
      return null;
    })
    .filter((group): group is DaySlot => group !== null);

  const receivedRDEData = context.provider_data_raw.map((rd) => {
    const filteredRDESlots = rdeSlots.filter((rdeSlot) => rdeSlot.rde.toString() === rd.id.toString())[0];

    return {
      ...rd,
      days: filteredRDESlots?.days || [],
      dayCount: filteredRDESlots?.dayCount || 0,
    };
  });

  type ReceivedRDEData = ProviderData & {
    days: DayInRDE[];
    dayCount: number;
  };

  const dedupRDEData = Array.from(
    receivedRDEData
      .reduce((map, item) => {
        const key = item.id || item.healthie_id;
        if (!map.has(key)) {
          map.set(key, item);
        }
        return map;
      }, new Map())
      .values(),
  ) as ReceivedRDEData[];

  return {
    ...context,
    day_slots: daySlots,
    rde_slots: rdeSlots,
    slots: filteredSlots,
    provider_data: dedupRDEData,
    applied_slot_filters: true,
  };
};
